import React, { useEffect, useState } from 'react';
import { getAddonCatalog, createAddonCatalog, updateAddonCatalog } from '../../../services/api';
import { Plus, Pencil, X } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY_FORM = {
  code: '', name: '', description: '', icon: '',
  price_monthly: 0, price_yearly: 0,
  compatible_pos_types: ['retail', 'restaurant'],
  requires_stamps: false, terminal_quantity: 0,
  max_orders_monthly: '', overage_cost_per_order: 0,
  is_active: true, sort_order: 0,
};

const AddonCatalogTab = () => {
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadAddons(); }, []);

  const loadAddons = async () => {
    setLoading(true);
    try {
      const res = await getAddonCatalog();
      setAddons(res.data?.data || []);
    } catch { setAddons([]); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setShowModal(true);
  };

  const openEdit = (addon) => {
    setEditing(addon);
    setForm({
      code: addon.code,
      name: addon.name,
      description: addon.description || '',
      icon: addon.icon || '',
      price_monthly: addon.price_monthly || 0,
      price_yearly: addon.price_yearly || 0,
      compatible_pos_types: addon.compatible_pos_types || ['retail', 'restaurant'],
      requires_stamps: addon.requires_stamps || false,
      terminal_quantity: addon.terminal_quantity || 0,
      max_orders_monthly: addon.max_orders_monthly ?? '',
      overage_cost_per_order: addon.overage_cost_per_order || 0,
      is_active: addon.is_active !== false,
      sort_order: addon.sort_order || 0,
    });
    setShowModal(true);
  };

  const handlePosTypeToggle = (type) => {
    const current = form.compatible_pos_types || [];
    if (current.includes(type)) {
      if (current.length <= 1) return;
      setForm({ ...form, compatible_pos_types: current.filter(t => t !== type) });
    } else {
      setForm({ ...form, compatible_pos_types: [...current, type] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.name.trim()) return toast.error('Código y nombre son requeridos');
    setSaving(true);
    try {
      const payload = {
        ...form,
        max_orders_monthly: form.max_orders_monthly === '' ? null : parseInt(form.max_orders_monthly) || null,
      };
      if (editing) {
        const { code, ...updateData } = payload;
        await updateAddonCatalog(editing.code, updateData);
        toast.success('Addon actualizado');
      } else {
        await createAddonCatalog(payload);
        toast.success('Addon creado');
      }
      setShowModal(false);
      loadAddons();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar addon');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-900">Catálogo de Addons</h2>
        <button onClick={openCreate} className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-700 text-white text-sm rounded-lg hover:bg-teal-800 font-medium transition-colors">
          <Plus className="w-4 h-4" /> Nuevo Addon
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Código</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Nombre</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Descripción</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Mensual</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Anual</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Tipos POS</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Terminales</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Pedidos/mes</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">$/Excedente</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Timbres</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Estado</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Acción</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {addons.length === 0 ? (
                <tr><td colSpan="12" className="px-4 py-12 text-center text-slate-500">No hay addons en el catálogo</td></tr>
              ) : addons.map((a) => (
                <tr key={a.code} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-sm font-mono text-slate-700">{a.code}</td>
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">{a.name}</td>
                  <td className="px-4 py-3 text-sm text-slate-500 max-w-[200px] truncate">{a.description || '—'}</td>
                  <td className="px-4 py-3 text-sm text-right text-slate-700">${(a.price_monthly || 0).toLocaleString('es-MX')}</td>
                  <td className="px-4 py-3 text-sm text-right text-slate-700">${(a.price_yearly || 0).toLocaleString('es-MX')}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {(a.compatible_pos_types || []).map(t => (
                        <span key={t} className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{t}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-slate-700">
                    {a.terminal_quantity > 0 ? `+${a.terminal_quantity}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-slate-700">
                    {a.max_orders_monthly ? a.max_orders_monthly.toLocaleString('es-MX') : <span className="text-xs text-emerald-600 font-medium">Ilimitado</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-slate-700">
                    {a.overage_cost_per_order > 0 ? `$${a.overage_cost_per_order}` : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {a.requires_stamps ? (
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Sí</span>
                    ) : (
                      <span className="text-xs text-slate-400">No</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${a.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {a.is_active !== false ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openEdit(a)} className="p-1.5 hover:bg-slate-100 rounded text-slate-500" title="Editar">
                      <Pencil className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold text-slate-900">{editing ? 'Editar Addon' : 'Nuevo Addon'}</h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Código *</label>
                  <input
                    type="text" required disabled={!!editing}
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:bg-slate-50 disabled:text-slate-500"
                    placeholder="mod-invoicing"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Nombre *</label>
                  <input
                    type="text" required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Facturación Electrónica"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Descripción</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="Descripción del addon"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Precio Mensual (MXN)</label>
                  <input
                    type="number" step="0.01" min="0"
                    value={form.price_monthly}
                    onChange={(e) => setForm({ ...form, price_monthly: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Precio Anual (MXN)</label>
                  <input
                    type="number" step="0.01" min="0"
                    value={form.price_yearly}
                    onChange={(e) => setForm({ ...form, price_yearly: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Tipos de POS compatibles</label>
                <div className="flex gap-3">
                  {['retail', 'restaurant'].map(type => (
                    <label key={type} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={(form.compatible_pos_types || []).includes(type)}
                        onChange={() => handlePosTypeToggle(type)}
                        className="rounded border-slate-300"
                      />
                      {type === 'retail' ? 'Retail' : 'Restaurante'}
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Terminales extra</label>
                  <input
                    type="number" min="0"
                    value={form.terminal_quantity}
                    onChange={(e) => setForm({ ...form, terminal_quantity: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="0"
                  />
                  <p className="text-xs text-slate-400 mt-1">Terminales adicionales que otorga este addon</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Orden</label>
                  <input
                    type="number" min="0"
                    value={form.sort_order}
                    onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Pedidos/mes incluidos</label>
                  <input
                    type="number" min="0"
                    value={form.max_orders_monthly}
                    onChange={(e) => setForm({ ...form, max_orders_monthly: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Vacío = ilimitado"
                  />
                  <p className="text-xs text-slate-400 mt-1">Dejar vacío para ilimitado</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Costo por excedente</label>
                  <input
                    type="number" step="0.01" min="0"
                    value={form.overage_cost_per_order}
                    onChange={(e) => setForm({ ...form, overage_cost_per_order: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="0.00"
                  />
                  <p className="text-xs text-slate-400 mt-1">MXN por pedido sobre el límite</p>
                </div>
              </div>

              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.requires_stamps}
                    onChange={(e) => setForm({ ...form, requires_stamps: e.target.checked })}
                    className="rounded border-slate-300"
                  />
                  Requiere timbres
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    className="rounded border-slate-300"
                  />
                  Activo
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors disabled:opacity-50">
                  {saving ? 'Guardando...' : editing ? 'Actualizar' : 'Crear'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium transition-colors">
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddonCatalogTab;
